import React, { useState, useRef, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { Loader2, Plus, ArrowDownToLine, Wrench, SlidersHorizontal, Pencil, StickyNote, Send,
  Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Search, Warehouse, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNITS = ["m", "mm", "cm", "ft", "in", "kg", "g", "ton", "pcs", "nos", "sheets", "coils", "litre"];
const DIM_SYMBOLS = ["Ø", "×", "″", "°", "½", "¼", "¾", "⅜", "⅝"];
export const SHAPES = ["Round bar", "Pipe", "Tube", "Plate", "Flat bar", "Hex bar", "Square bar", "Angle", "Other"];

// Quantity fields accept simple maths like "148*5" (cut length × pieces) — the
// way staff already log issues in Excel. Whitelisted characters only, no eval
// of anything beyond numbers and + - * / ( ).
export function evalQty(raw) {
  const s = String(raw ?? "").trim().replace(/,/g, "");
  if (!s) return NaN;
  if (!/^[\d\s+\-*/().]+$/.test(s)) return NaN;
  try {
    const v = Function('"use strict";return (' + s + ")")();
    return typeof v === "number" && isFinite(v) ? v : NaN;
  } catch { return NaN; }
}

// Numeric input with live "= result" preview when an expression is typed.
function QtyInput({ value, onChange, placeholder }) {
  const v = evalQty(value);
  const hasOp = /[+*/]|\d\s*-/.test(String(value ?? ""));
  return (
    <div>
      <input value={value} onChange={(e) => onChange(e.target.value)} inputMode="decimal"
        placeholder={placeholder || "e.g. 148*5 or 740"} className={`${inputCls} font-mono-data`} />
      {hasOp && !isNaN(v) && <div className="text-[11px] text-emerald-600 mt-1 font-mono-data">= {Math.round(v * 10000) / 10000}</div>}
      {String(value ?? "").trim() !== "" && isNaN(v) && (
        <div className="text-[11px] text-amber-600 mt-1">Numbers and + − × / only, e.g. 148*5</div>
      )}
    </div>
  );
}

const inputCls = "w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500";
const labelCls = "text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block";

function Field({ label, children, span2 }) {
  return (
    <div className={span2 ? "col-span-2" : ""}>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

function Text({ value, onChange, mono, ...rest }) {
  return <input value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls}${mono ? " font-mono-data" : ""}`} {...rest} />;
}

// The factory buys in rupees — every supplier invoice is in ₹ — so the cost
// box speaks ₹ first and the symbol sits INSIDE the field where the number is.
// The flip is for the occasional imported lot that was actually priced in
// dollars; it is a switch, not a converter — whatever is typed is what is
// stored, tagged with the currency it was typed in.
const COST_CURRENCIES = [["INR", "₹", "Cost is in rupees"], ["USD", "$", "Cost is in US dollars"]];

export function CostField({ value, onChange, currency, onCurrency, placeholder }) {
  const cur = currency === "USD" ? "USD" : "INR";
  return (
    <div className="flex items-stretch gap-1.5">
      <div className="relative flex-1 min-w-0">
        <span aria-hidden className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] font-semibold text-[hsl(var(--muted-foreground))] pointer-events-none">
          {cur === "USD" ? "$" : "₹"}
        </span>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          type="number"
          step="any"
          placeholder={placeholder}
          aria-label={cur === "USD" ? "Unit cost in US dollars" : "Unit cost in rupees"}
          data-testid="cost-amount"
          className={`${inputCls} pl-7 font-mono-data`}
        />
      </div>
      <div role="group" aria-label="Cost currency"
        className="shrink-0 inline-flex items-center gap-0.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-0.5">
        {COST_CURRENCIES.map(([code, sym, hint]) => (
          <button
            key={code}
            type="button"
            onClick={() => onCurrency(code)}
            aria-pressed={cur === code}
            title={hint}
            data-testid={`cost-currency-${code.toLowerCase()}`}
            className={`w-8 h-[30px] rounded text-[13px] font-bold transition-colors ${
              cur === code
                ? "bg-orange-600 text-white"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            }`}
          >
            {sym}
          </button>
        ))}
      </div>
    </div>
  );
}

function Check({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm text-[hsl(var(--foreground))] cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-orange-600" />
      {label}
    </label>
  );
}

// Dimensions input + quick-insert symbol chips (inserts at the cursor).
function DimsField({ value, onChange, placeholder }) {
  const ref = useRef(null);
  const insert = (sym) => {
    const el = ref.current;
    const v = value || "";
    if (el && typeof el.selectionStart === "number") {
      const s = el.selectionStart, e = el.selectionEnd;
      const next = v.slice(0, s) + sym + v.slice(e);
      onChange(next);
      requestAnimationFrame(() => { try { el.focus(); const p = s + sym.length; el.setSelectionRange(p, p); } catch (_) {} });
    } else {
      onChange(v + sym);
    }
  };
  return (
    <div>
      <input ref={ref} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className={`${inputCls} font-mono-data`} />
      <div className="flex flex-wrap gap-1 mt-1.5">
        {DIM_SYMBOLS.map((s) => (
          <button type="button" key={s}
            onMouseDown={(e) => { e.preventDefault(); insert(s); }}
            className="w-7 h-7 flex items-center justify-center text-[13px] rounded-md border border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] hover:border-orange-500/50 font-mono-data transition-colors"
            title={`Insert ${s}`}>{s}</button>
        ))}
      </div>
    </div>
  );
}

function SubmitBtn({ saving, children, icon: Icon }) {
  return (
    <button type="submit" disabled={saving}
      className="inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white transition-colors">
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : Icon ? <Icon className="w-4 h-4" /> : null}
      {children}
    </button>
  );
}

function CancelBtn({ onClick }) {
  return (
    <button type="button" onClick={onClick}
      className="text-[13px] font-medium px-4 py-2 rounded-lg bg-[hsl(var(--muted))] border border-[hsl(var(--border))] hover:bg-[hsl(var(--accent))] text-[hsl(var(--foreground))] transition-colors">
      Cancel
    </button>
  );
}

function lotTitle(lot) {
  return `${lot?.material || ""}${lot?.grade ? ` · ${lot.grade}` : ""}`;
}

// Magic search over live line-items. Matches PO / part / description / client.
// Calls onPick(item|null) when a result is chosen/cleared, and onText(str) as
// the user types (so a not-yet-in-system PO can still be captured manually).
function ItemPicker({ onPick, onText }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const deb = useRef(null);
  const onChange = (val) => {
    setQ(val); setPicked(null); onText?.(val); onPick?.(null);
    if (deb.current) clearTimeout(deb.current);
    if (val.trim().length < 2) { setResults([]); setOpen(false); return; }
    deb.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/stores/item-search?q=${encodeURIComponent(val)}`);
        setResults(Array.isArray(r.data) ? r.data : []);
        setOpen(true);
      } catch { setResults([]); }
    }, 200);
  };
  const choose = (it) => {
    setPicked(it);
    setQ(`${it.po_number || ""}${it.line_number ? ` · L${it.line_number}` : ""}`);
    setOpen(false);
    onPick?.(it);
  };
  return (
    <div className="relative">
      <input value={q} onChange={(e) => onChange(e.target.value)} onFocus={() => results.length && setOpen(true)}
        placeholder="Search PO, part, or client…" className={inputCls} data-testid="item-picker-input" />
      {picked && <div className="text-[11px] text-emerald-600 mt-1 truncate">✓ {picked.item_description}{picked.client ? ` · ${picked.client}` : ""}</div>}
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] shadow-lg">
          {results.map((it) => (
            <button type="button" key={it.id} onClick={() => choose(it)}
              className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--accent))] border-b border-[hsl(var(--border))]/40 last:border-0">
              <div className="text-[12.5px] flex items-center gap-2">
                <span className="font-mono-data">{it.po_number || "—"}</span>
                {it.line_number && <span className="font-mono-data text-[hsl(var(--muted-foreground))]">L{it.line_number}</span>}
                {it.client && <span className="text-[hsl(var(--muted-foreground))]">· {it.client}</span>}
              </div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{it.part_number ? `${it.part_number} · ` : ""}{it.item_description}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Receive stock ──
export function ReceiveStockModal({ onClose, onDone, initial }) {
  // `initial` = duplicate-from-lot: everything copies except the physical tag
  // (bar/tag no. is unique per piece) and quantity.
  const [f, setF] = useState(() => ({
    material: initial?.material || "", grade: initial?.grade || "", spec: initial?.spec || "",
    shape: initial?.shape || "", bar_no: "", customer: initial?.customer || "",
    heat_number: initial?.heat_number || "", mtc_on_file: !!initial?.mtc_on_file,
    mtc_ref: initial?.mtc_ref || "", mtc_link: initial?.mtc_link || "",
    dimensions: initial?.dimensions || "", unit: initial?.unit || "m", quantity: "",
    unit_cost: initial?.unit_cost ?? "", unit_cost_currency: initial?.unit_cost_currency || "INR",
    supplier: initial?.supplier || "", location: initial?.location || "",
    is_remnant: !!initial?.is_remnant, pre_expensed: !!initial?.pre_expensed,
    source_po: initial?.source_po || "", low_threshold: initial?.low_threshold || "",
    received_at: new Date().toISOString().split("T")[0],
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const qty = evalQty(f.quantity);
  const valid = f.material.trim() && qty > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { toast.error("Material and a positive quantity are required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/stores/lots/receive`, {
        ...f,
        quantity: qty,
        unit_cost: evalQty(f.unit_cost) || 0,
        unit_cost_currency: f.unit_cost_currency || "INR",
        low_threshold: evalQty(f.low_threshold) || 0,
      });
      toast.success(`Received ${f.quantity} ${f.unit} of ${f.material}`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to receive stock");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-xl" data-testid="receive-stock-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-orange-500" /> {initial ? "Duplicate lot" : "Receive stock"}</DialogTitle>
          <DialogDescription>{initial ? "Copied from the existing lot — bar/tag no. and quantity start blank. Change whatever differs." : "Log raw material into Stores. Remnants carry the parent heat number."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Material *"><Text value={f.material} onChange={(v) => set("material", v)} placeholder="Round bar" /></Field>
            <Field label="Grade"><Text value={f.grade} onChange={(v) => set("grade", v)} placeholder="4140 alloy" /></Field>
            <Field label="Shape">
              <select value={f.shape} onChange={(e) => set("shape", e.target.value)} className={inputCls}>
                <option value="">— pick a shape —</option>
                {SHAPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Bar / tag no."><Text value={f.bar_no} onChange={(v) => set("bar_no", v)} mono placeholder="tag on the piece, e.g. B4" /></Field>
            <Field label="Spec / standard" span2><Text value={f.spec} onChange={(v) => set("spec", v)} placeholder="AISI 4140 / ASTM A29" /></Field>
            <Field label="Heat number"><Text value={f.heat_number} onChange={(v) => set("heat_number", v)} mono placeholder="H-44219" /></Field>
            <Field label="Dimensions"><DimsField value={f.dimensions} onChange={(v) => set("dimensions", v)} placeholder="Ø50 mm" /></Field>
            <Field label="Quantity *"><QtyInput value={f.quantity} onChange={(v) => set("quantity", v)} /></Field>
            <Field label="Unit">
              <select value={f.unit} onChange={(e) => set("unit", e.target.value)} className={inputCls}>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
            <Field label="Unit cost">
              <CostField value={f.unit_cost} onChange={(v) => set("unit_cost", v)}
                currency={f.unit_cost_currency} onCurrency={(c) => set("unit_cost_currency", c)}
                placeholder="12300" />
            </Field>
            <Field label="Low-stock threshold"><Text value={f.low_threshold} onChange={(v) => set("low_threshold", v)} mono type="number" step="any" placeholder="0 = off" /></Field>
            <Field label="Supplier"><Text value={f.supplier} onChange={(v) => set("supplier", v)} placeholder="SAIL" /></Field>
            <Field label="Customer (optional)"><Text value={f.customer} onChange={(v) => set("customer", v)} placeholder="e.g. BAKER · blank = surplus" /></Field>
            <Field label="Location / bin"><Text value={f.location} onChange={(v) => set("location", v)} placeholder="Rack B-3 / Central shed" /></Field>
            <Field label="Received on"><Text value={f.received_at} onChange={(v) => set("received_at", v)} type="date" /></Field>
            <Field label="MTC"><Check label="MTC on file" checked={f.mtc_on_file} onChange={(v) => set("mtc_on_file", v)} /></Field>
            <Field label="MTC link (SharePoint / OneDrive)" span2><Text value={f.mtc_link} onChange={(v) => set("mtc_link", v)} placeholder="https://rockpecker.sharepoint.com/…" /></Field>
            <div className="col-span-2 flex items-center gap-6 pt-1 border-t border-[hsl(var(--border))]/50">
              <Check label="This is a remnant / offcut" checked={f.is_remnant} onChange={(v) => set("is_remnant", v)} />
            </div>
            {f.is_remnant && (
              <>
                <Field label="Source PO"><Text value={f.source_po} onChange={(v) => set("source_po", v)} mono placeholder="PO-2026-006" /></Field>
                <Field label="Cost already absorbed?"><Check label="Pre-expensed (carry at ₹0)" checked={f.pre_expensed} onChange={(v) => set("pre_expensed", v)} /></Field>
              </>
            )}
          </div>
        </form>
        <DialogFooter>
          <CancelBtn onClick={onClose} />
          <form onSubmit={submit}><SubmitBtn saving={saving} icon={Plus}>Receive</SubmitBtn></form>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Reserve ──
export function ReserveModal({ lot, onClose, onDone, boundItem = null }) {
  // `boundItem` = the PO line that opened this modal (e.g. from the procurement
  // "Check Stores" panel). When set, the reservation is pre-bound to that line —
  // no searching — with an escape hatch to pick a different order.
  const free = Math.max(0, (lot.quantity_on_hand || 0) - (lot.quantity_reserved || 0));
  const [quantity, setQuantity] = useState("");
  const [refNo, setRefNo] = useState("");
  const [note, setNote] = useState("");
  const [sel, setSel] = useState(null);
  const [text, setText] = useState("");
  const [useBound, setUseBound] = useState(!!boundItem);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const q = evalQty(quantity);
    if (!(q > 0)) { toast.error("Enter a positive quantity (maths like 148*5 works)"); return; }
    if (q > free) { toast.error(`Only ${free} ${lot.unit} free to reserve`); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/stores/lots/${lot.id}/reserve`, {
        quantity: q, note,
        po_number: useBound ? (boundItem.po_number || "") : (sel?.po_number || text.trim()),
        line_number: useBound ? (boundItem.line_number || "") : (sel?.line_number || ""),
        item_id: useBound ? (boundItem.id || "") : (sel?.id || ""),
        ref_no: refNo.trim(),
      });
      toast.success(`Reserved ${q} ${lot.unit}`);
      onDone?.(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to reserve"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[480px] max-w-[95vw] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowDownToLine className="w-5 h-5 text-blue-500 shrink-0" /> <span className="truncate">Reserve from {lotTitle(lot)}</span></DialogTitle>
          <DialogDescription>{free} {lot.unit} free · Heat {lot.heat_number || "—"}. Held until issued or kitting.</DialogDescription>
        </DialogHeader>
        {/* min-w-0: the DialogContent is a grid — without it, a long bound-item
            description sets the form's min-content width and blows the dialog
            past its cap instead of truncating. */}
        <form onSubmit={submit} className="space-y-4 min-w-0">
          {useBound ? (
            <Field label="For order" span2>
              <div className="rounded-md border border-blue-500/30 bg-blue-500/8 px-3 py-2 min-w-0 overflow-hidden" data-testid="reserve-bound-order">
                <div className="font-mono-data text-[12.5px] font-semibold text-blue-700 dark:text-blue-300 truncate">
                  {boundItem.po_number || "—"}{boundItem.line_number ? ` · L${boundItem.line_number}` : ""}
                </div>
                <div className="text-[11px] text-blue-600/90 dark:text-blue-300/80 truncate mt-0.5">
                  ✓ {[boundItem.part_number, boundItem.item_description].filter(Boolean).join(" · ") || "this line"}{boundItem.client ? ` · ${boundItem.client}` : ""}
                </div>
              </div>
              <button type="button" onClick={() => setUseBound(false)}
                className="text-[11px] text-[hsl(var(--muted-foreground))] underline decoration-dotted hover:text-[hsl(var(--foreground))] mt-1"
                data-testid="reserve-unbind-btn">
                reserve for a different order…
              </button>
            </Field>
          ) : (
            <Field label="For order (search PO / part / client)" span2><ItemPicker onPick={setSel} onText={setText} /></Field>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label={`Quantity (${lot.unit}) *`}><QtyInput value={quantity} onChange={setQuantity} /></Field>
            <Field label="MIS / slip no."><Text value={refNo} onChange={setRefNo} mono placeholder="e.g. 2384 (optional)" /></Field>
            <Field label="Note" span2><Text value={note} onChange={setNote} placeholder="optional — e.g. issued to / remarks" /></Field>
          </div>
          <DialogFooter><CancelBtn onClick={onClose} /><SubmitBtn saving={saving} icon={ArrowDownToLine}>Reserve</SubmitBtn></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Issue ──
export function IssueModal({ lot, onClose, onDone }) {
  const onHand = lot.quantity_on_hand || 0;
  const [quantity, setQuantity] = useState("");
  const [kittedPart, setKittedPart] = useState("");
  const [refNo, setRefNo] = useState("");
  const [note, setNote] = useState("");
  const [sel, setSel] = useState(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const q = evalQty(quantity);
    if (!(q > 0)) { toast.error("Enter a positive quantity (maths like 148*5 works)"); return; }
    if (q > onHand) { toast.error(`Only ${onHand} ${lot.unit} on hand`); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/stores/lots/${lot.id}/issue`, {
        quantity: q, note,
        po_number: sel?.po_number || text.trim(),
        line_number: sel?.line_number || "",
        item_id: sel?.id || "",
        kitted_part: kittedPart.trim() || sel?.part_number || "",
        ref_no: refNo.trim(),
      });
      toast.success(`Issued ${q} ${lot.unit}`);
      onDone?.(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to issue"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wrench className="w-5 h-5 text-emerald-500 shrink-0" /> <span className="truncate">Issue from {lotTitle(lot)}</span></DialogTitle>
          <DialogDescription>{onHand} {lot.unit} on hand. Consuming material to a job.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 min-w-0">
          <Field label="For order (search PO / part / client)" span2><ItemPicker onPick={setSel} onText={setText} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={`Quantity (${lot.unit}) *`}><QtyInput value={quantity} onChange={setQuantity} /></Field>
            <Field label="MIS / slip no."><Text value={refNo} onChange={setRefNo} mono placeholder="e.g. 2384 (optional)" /></Field>
            <Field label="Kitted into (part)"><Text value={kittedPart} onChange={setKittedPart} mono placeholder={sel?.part_number || "F-123456"} /></Field>
            <Field label="Note"><Text value={note} onChange={setNote} placeholder="e.g. issued to Rahul" /></Field>
          </div>
          <DialogFooter><CancelBtn onClick={onClose} /><SubmitBtn saving={saving} icon={Wrench}>Issue</SubmitBtn></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Adjust ──
export function AdjustModal({ lot, onClose, onDone }) {
  const onHand = lot.quantity_on_hand || 0;
  const [f, setF] = useState({ delta: "", reason: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const delta = evalQty(f.delta);
  const preview = Number.isFinite(delta) ? Math.round((onHand + delta) * 10000) / 10000 : onHand;
  const submit = async (e) => {
    e.preventDefault();
    if (!Number.isFinite(delta) || delta === 0) { toast.error("Enter a non-zero change (use - to reduce)"); return; }
    if (!f.reason.trim()) { toast.error("A reason is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/stores/lots/${lot.id}/adjust`, { delta, reason: f.reason });
      toast.success(`Adjusted by ${delta > 0 ? "+" : ""}${delta} ${lot.unit}`);
      onDone?.(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to adjust"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-amber-500" /> Adjust {lotTitle(lot)}</DialogTitle>
          <DialogDescription>On hand {onHand} {lot.unit} → <span className="font-mono-data text-[hsl(var(--foreground))]">{preview} {lot.unit}</span>. Use a negative value to reduce.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label={`Change (${lot.unit}) *`}><QtyInput value={f.delta} onChange={(v) => set("delta", v)} placeholder="-0.2 (minus = reduce)" /></Field>
            <Field label="Reason *"><Text value={f.reason} onChange={(v) => set("reason", v)} placeholder="Cutting loss" /></Field>
          </div>
          <DialogFooter><CancelBtn onClick={onClose} /><SubmitBtn saving={saving} icon={SlidersHorizontal}>Adjust</SubmitBtn></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Scrap lot ──
// Mark a small offcut as physically disposed of / sold for salvage. The lot is
// NOT deleted — its heat #/qty/audit trail stays — it just leaves the store view.
// Per the chairman, the salvage value is intentionally NOT recorded.
export function ScrapLotModal({ lot, onClose, onDone }) {
  const onHand = lot.quantity_on_hand || 0;
  const reserved = lot.quantity_reserved || 0;
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const blocked = reserved > 0;
  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { toast.error("A reason is required to scrap"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/stores/lots/${lot.id}/scrap`, { reason: reason.trim() });
      toast.success(`Scrapped ${onHand} ${lot.unit}`);
      onDone?.(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to scrap"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-500" /> Scrap {lotTitle(lot)}</DialogTitle>
          <DialogDescription>
            Marks this lot as disposed of (sent for scrap / sold for salvage). On hand{" "}
            <span className="font-mono-data text-[hsl(var(--foreground))]">{onHand} {lot.unit}</span>{" "}
            goes to 0 and the lot leaves the store. The full record (heat #, quantity, who &amp; why) is kept for audit. Reversible.
          </DialogDescription>
        </DialogHeader>
        {blocked ? (
          <div className="flex items-start gap-2 text-[13px] text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>This lot has {reserved} {lot.unit} reserved. Release the reservation(s) before scrapping.</span>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="Reason *" span2><Text value={reason} onChange={setReason} placeholder="e.g. Offcut too small to use — sent to scrap bin" /></Field>
            <DialogFooter>
              <CancelBtn onClick={onClose} />
              <button type="submit" disabled={saving}
                className="inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Scrap lot
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Edit lot ──
export function EditLotModal({ lot, onClose, onDone }) {
  const [f, setF] = useState({
    material: lot.material || "", grade: lot.grade || "", spec: lot.spec || "",
    shape: lot.shape || "", bar_no: lot.bar_no || "", customer: lot.customer || "",
    heat_number: lot.heat_number || "", mtc_on_file: !!lot.mtc_on_file, mtc_ref: lot.mtc_ref || "", mtc_link: lot.mtc_link || "",
    dimensions: lot.dimensions || "", unit: lot.unit || "m", unit_cost: lot.unit_cost ?? "",
    unit_cost_currency: lot.unit_cost_currency || "INR",
    supplier: lot.supplier || "", location: lot.location || "", low_threshold: lot.low_threshold ?? "",
    pre_expensed: !!lot.pre_expensed,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.patch(`${API}/stores/lots/${lot.id}`, {
        ...f,
        unit_cost: f.unit_cost === "" ? 0 : parseFloat(f.unit_cost),
        unit_cost_currency: f.unit_cost_currency || "INR",
        low_threshold: f.low_threshold === "" ? 0 : parseFloat(f.low_threshold),
      });
      toast.success("Lot updated");
      onDone?.(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-orange-500" /> Edit lot</DialogTitle>
          <DialogDescription>Quantities change via Receive / Issue / Adjust, not here.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Material"><Text value={f.material} onChange={(v) => set("material", v)} /></Field>
            <Field label="Grade"><Text value={f.grade} onChange={(v) => set("grade", v)} /></Field>
            <Field label="Shape">
              <select value={f.shape} onChange={(e) => set("shape", e.target.value)} className={inputCls}>
                <option value="">— pick a shape —</option>
                {SHAPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Bar / tag no."><Text value={f.bar_no} onChange={(v) => set("bar_no", v)} mono placeholder="tag on the piece, e.g. B4" /></Field>
            <Field label="Spec / standard" span2><Text value={f.spec} onChange={(v) => set("spec", v)} /></Field>
            <Field label="Heat number"><Text value={f.heat_number} onChange={(v) => set("heat_number", v)} mono /></Field>
            <Field label="Dimensions"><DimsField value={f.dimensions} onChange={(v) => set("dimensions", v)} placeholder="Ø50 mm" /></Field>
            <Field label="Unit cost">
              <CostField value={f.unit_cost} onChange={(v) => set("unit_cost", v)}
                currency={f.unit_cost_currency} onCurrency={(c) => set("unit_cost_currency", c)} />
            </Field>
            <Field label="Low-stock threshold"><Text value={f.low_threshold} onChange={(v) => set("low_threshold", v)} mono type="number" step="any" /></Field>
            <Field label="Supplier"><Text value={f.supplier} onChange={(v) => set("supplier", v)} /></Field>
            <Field label="Customer (optional)"><Text value={f.customer} onChange={(v) => set("customer", v)} placeholder="blank = surplus" /></Field>
            <Field label="Location / bin"><Text value={f.location} onChange={(v) => set("location", v)} /></Field>
            <Field label="MTC ref"><Text value={f.mtc_ref} onChange={(v) => set("mtc_ref", v)} /></Field>
            <Field label="MTC link (SharePoint / OneDrive)" span2><Text value={f.mtc_link} onChange={(v) => set("mtc_link", v)} placeholder="https://rockpecker.sharepoint.com/…" /></Field>
            <div className="col-span-2 flex items-center gap-6">
              <Check label="MTC on file" checked={f.mtc_on_file} onChange={(v) => set("mtc_on_file", v)} />
              <Check label="Pre-expensed (carry at ₹0)" checked={f.pre_expensed} onChange={(v) => set("pre_expensed", v)} />
            </div>
          </div>
          <DialogFooter><CancelBtn onClick={onClose} /><SubmitBtn saving={saving} icon={Pencil}>Save changes</SubmitBtn></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Read-only Stores lookup, embedded in procurement ──
export function CheckStoresPanel({ defaultQuery = "", boundItem = null, onReserved = null }) {
  // `boundItem` = the PO line being ordered (passed down from the ETA popup).
  // When present, each lot row gains a Reserve action pre-bound to that line —
  // the reservation then auto-issues when the line completes kitting.
  const { user } = useContext(AuthContext);
  const canReserve = user?.role === "Admin" || !!user?.permissions?.can_reserve_stock;
  const [q, setQ] = useState(defaultQuery);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reserveLot, setReserveLot] = useState(null);
  const deb = useRef(null);
  const run = (val) => {
    if (deb.current) clearTimeout(deb.current);
    deb.current = setTimeout(async () => {
      setLoading(true);
      try { const r = await axios.get(`${API}/stores/check?q=${encodeURIComponent(val || "")}`); setData(r.data); }
      catch { setData(null); } finally { setLoading(false); }
    }, 200);
  };
  useEffect(() => { run(defaultQuery); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const onChange = (v) => { setQ(v); run(v); };
  const lots = data?.lots || [];
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-3">
      <div className="flex items-center gap-2 mb-2">
        <Warehouse className="w-4 h-4 text-orange-500" />
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">Check Stores before ordering</span>
        {boundItem?.po_number && (
          <span className="ml-auto font-mono-data text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-300 whitespace-nowrap"
            title="Reservations made here are linked to this PO line and auto-issue at kitting">
            for {boundItem.po_number}{boundItem.line_number ? ` · L${boundItem.line_number}` : ""}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-md px-2.5 py-1.5">
        <Search className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
        <input value={q} onChange={(e) => onChange(e.target.value)} placeholder="Search material, grade, heat #…" className="bg-transparent outline-none text-[12.5px] w-full" />
      </div>
      <div className="mt-2 max-h-[40vh] overflow-y-auto">
        {loading ? (
          <div className="text-[11px] text-[hsl(var(--muted-foreground))] py-2 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Checking…</div>
        ) : lots.length === 0 ? (
          <div className="text-[11px] text-[hsl(var(--muted-foreground))] py-2">No matching stock{q ? "" : " — type to search"}.</div>
        ) : (
          <>
            <div className="text-[11px] font-semibold text-emerald-600 mb-1.5 font-mono-data">{data.total_free} total free across {lots.length} lot{lots.length !== 1 ? "s" : ""}</div>
            {lots.map((l) => (
              /* Dense single-line row: name · remnant · heat/dims/bin (mono, truncates) · free · Reserve */
              <div key={l.id} className="flex items-center gap-2 py-1 border-b border-[hsl(var(--border))]/40 last:border-0">
                <span className="text-[12px] font-medium text-[hsl(var(--foreground))] truncate shrink-0 max-w-[36%]">{l.material}{l.grade ? ` · ${l.grade}` : ""}</span>
                {l.is_remnant && (
                  <span className="text-[8.5px] font-bold uppercase tracking-wide bg-orange-500/15 text-orange-600 px-1 py-px rounded shrink-0">Remnant</span>
                )}
                <span className="text-[10px] text-[hsl(var(--muted-foreground))] font-mono-data truncate flex-1 min-w-0">
                  {[l.heat_number, l.dimensions, l.location].filter(Boolean).join(" · ")}
                </span>
                <span className="text-[12px] font-semibold font-mono-data text-emerald-600 whitespace-nowrap shrink-0">{l.free} {l.unit}</span>
                {canReserve && (
                  <button type="button" onClick={() => setReserveLot(l)}
                    className="inline-flex items-center gap-1 text-[10.5px] font-medium px-1.5 py-0.5 rounded-md border border-blue-500/30 bg-blue-500/8 text-blue-700 dark:text-blue-300 hover:bg-blue-500/15 hover:border-blue-500/50 transition-colors shrink-0"
                    title={boundItem?.po_number ? `Reserve from this lot for ${boundItem.po_number}` : "Reserve from this lot"}
                    data-testid={`check-stores-reserve-${l.id}`}>
                    <ArrowDownToLine className="w-3 h-3" /> Reserve
                  </button>
                )}
              </div>
            ))}
          </>
        )}
      </div>
      <a href="/stores" className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline mt-2"><Warehouse className="w-3 h-3" /> Open Stores</a>
      {reserveLot && (
        <ReserveModal
          lot={reserveLot}
          boundItem={boundItem}
          onClose={() => setReserveLot(null)}
          onDone={() => { run(q); onReserved?.(); }}
        />
      )}
    </div>
  );
}

// ── Excel / CSV import ──
const IMPORT_COLS = ["material", "grade", "spec", "shape", "bar_no", "customer", "heat_number", "mtc_on_file", "mtc_link", "dimensions", "unit", "quantity", "unit_cost", "unit_cost_currency", "supplier", "location", "is_remnant", "pre_expensed", "source_po", "low_threshold", "received_at"];
const toBool = (v) => /^(true|yes|y|1)$/i.test(String(v || "").trim());
const toNum = (v) => { const n = parseFloat(String(v || "").replace(/[^0-9.\-]/g, "")); return isNaN(n) ? 0 : n; };
function parseCSV(text) {
  // Strip the UTF-8 BOM Excel prepends, normalise newlines.
  const raw = String(text || "").replace(/^﻿/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = raw.split("\n").filter((l) => l.trim().length);
  if (!lines.length) return { headers: [], rows: [] };
  // Auto-detect the delimiter — Excel uses ";" or tab in some locales.
  const head = lines[0];
  const n = (ch) => head.split(ch).length - 1;
  const delim = n(";") > n(",") ? ";" : n("\t") > n(",") ? "\t" : ",";
  const parseLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) { if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else inQ = false; } else cur += c; }
      else { if (c === '"') inQ = true; else if (c === delim) { out.push(cur); cur = ""; } else cur += c; }
    }
    out.push(cur); return out.map((s) => s.trim().replace(/^["']|["']$/g, ""));
  };
  // Normalise headers: lowercase, strip BOM/quotes, spaces → underscores.
  const cleanHead = (h) => h.toLowerCase().replace(/^﻿/, "").trim().replace(/^["']|["']$/g, "").replace(/\s+/g, "_");
  return { headers: parseLine(lines[0]).map(cleanHead), rows: lines.slice(1).map(parseLine) };
}

// Excel rewrites ISO dates into locale formats. Accept the common ones and
// normalise back to YYYY-MM-DD. Ambiguous numeric dates (e.g. 05/06/2026) are
// read DAY-FIRST — the India/UK convention. Returns "" if unparseable, so the
// backend falls back to today rather than storing junk.
const _pad = (x) => String(x).padStart(2, "0");
const _utcIso = (y, mo, d) => `${y}-${_pad(mo)}-${_pad(d)}`;
const _MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
function normalizeDate(raw) {
  const s = String(raw || "").trim().replace(/^["']|["']$/g, "");
  if (!s) return "";
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);                 // already ISO
  if (m) return _utcIso(m[1], +m[2], +m[3]);
  if (/^\d{4,6}$/.test(s)) {                                        // Excel serial number
    const serial = parseInt(s, 10);
    if (serial > 20000 && serial < 80000) {
      const d = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
      return _utcIso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
    }
  }
  const mon = s.toLowerCase().match(/[a-z]{3,9}/);                  // month-name formats
  if (mon && _MONTHS[mon[0].slice(0, 3)]) {
    const nums = (s.match(/\d+/g) || []).map((x) => parseInt(x, 10));
    const day = nums.find((x) => x >= 1 && x <= 31);
    let year = nums.find((x) => x > 31);
    if (year === undefined) year = nums.find((x) => x !== day);
    if (day && year !== undefined) { if (year < 100) year += 2000; return _utcIso(year, _MONTHS[mon[0].slice(0, 3)], day); }
  }
  m = s.match(/^(\d{1,4})[/.\-](\d{1,2})[/.\-](\d{1,4})$/);         // numeric with / - .
  if (m) {
    const a = +m[1], b = +m[2]; let c = +m[3];
    if (m[1].length === 4) return _utcIso(a, b, c);                 // YYYY/MM/DD
    if (c < 100) c += 2000;
    let day, month;
    if (a > 12) { day = a; month = b; }            // unambiguous day-first
    else if (b > 12) { month = a; day = b; }       // unambiguous month-first (US)
    else { day = a; month = b; }                   // ambiguous → day-first
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) return _utcIso(c, month, day);
  }
  const d = new Date(s);                                            // last resort
  return isNaN(d.getTime()) ? "" : _utcIso(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function rowToLot(headers, values) {
  const o = {}; headers.forEach((h, i) => { o[h] = values[i] ?? ""; });
  return {
    material: (o.material || "").trim(), grade: (o.grade || "").trim(), spec: (o.spec || "").trim(),
    shape: (o.shape || "").trim(), bar_no: (o.bar_no || "").trim(), customer: (o.customer || "").trim(),
    heat_number: (o.heat_number || "").trim(), mtc_on_file: toBool(o.mtc_on_file), mtc_link: (o.mtc_link || "").trim(),
    dimensions: (o.dimensions || "").trim(), unit: (o.unit || "m").trim() || "m", quantity: toNum(o.quantity),
    unit_cost: toNum(o.unit_cost),
    // Blank means "whatever the modal's toggle says" — resolved at import time.
    unit_cost_currency: /^usd|^\$/i.test(String(o.unit_cost_currency || "").trim()) ? "USD"
      : /^inr|^₹|^rs/i.test(String(o.unit_cost_currency || "").trim()) ? "INR" : "",
    supplier: (o.supplier || "").trim(), location: (o.location || "").trim(),
    is_remnant: toBool(o.is_remnant), pre_expensed: toBool(o.pre_expensed), source_po: (o.source_po || "").trim(),
    low_threshold: toNum(o.low_threshold), received_at: normalizeDate(o.received_at),
  };
}
export function StoresImportModal({ onClose, onDone }) {
  const [parsed, setParsed] = useState(null);
  const [importing, setImporting] = useState(false);
  // Sheets from the office are in rupees. A per-row unit_cost_currency column
  // still wins where someone has bothered to fill it in.
  const [costCurrency, setCostCurrency] = useState("INR");
  const fileRef = useRef(null);
  const downloadTemplate = () => {
    const ex1 = ["Round bar", "4140 alloy", "AISI 4140 / ASTM A29", "Round bar", "B4", "BAKER", "H-44219", "true", "", "Ø50 mm", "m", "12", "12300", "INR", "SAIL", "Rack B-3", "false", "false", "", "", "2026-05-28"];
    const ex2 = ["Plate", "Inconel 718", "UNS N07718", "Plate", "", "", "H-77051", "false", "", "200×140×25", "pcs", "1", "0", "INR", "Special Metals", "Cabinet C", "true", "true", "PO-2026-011", "", "2026-05-19"];
    const csv = [IMPORT_COLS.join(","), ex1.join(","), ex2.join(",")].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "rocklink_stores_import_template.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCSV(e.target.result);
      if (!headers.includes("material")) { toast.error("CSV needs a 'material' column — use the template"); return; }
      const lots = rows.map((r) => rowToLot(headers, r));
      const valid = lots.filter((l) => l.material && l.quantity > 0);
      setParsed({ total: lots.length, valid, errorCount: lots.length - valid.length });
    };
    reader.readAsText(file);
  };
  const doImport = async () => {
    if (!parsed?.valid?.length) return;
    setImporting(true);
    try {
      const rows = parsed.valid.map((l) => ({ ...l, unit_cost_currency: l.unit_cost_currency || costCurrency }));
      const CHUNK = 500; let total = 0;
      for (let i = 0; i < rows.length; i += CHUNK) {
        const res = await axios.post(`${API}/stores/lots/bulk`, rows.slice(i, i + CHUNK));
        total += res.data?.count ?? 0;
      }
      toast.success(`Imported ${total} lot${total !== 1 ? "s" : ""}`);
      onDone?.(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-orange-500" /> Import stock from Excel / CSV</DialogTitle>
          <DialogDescription>Export your sheet as CSV, then upload it. Each row needs a material name and a positive quantity.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--accent))] text-[hsl(var(--foreground))]"><Download className="w-4 h-4" /> Download CSV template</button>
          <div onClick={() => fileRef.current?.click()} className="cursor-pointer rounded-lg border-2 border-dashed border-[hsl(var(--border))] hover:border-orange-500/50 px-4 py-6 text-center transition-colors">
            <Upload className="w-5 h-5 mx-auto text-[hsl(var(--muted-foreground))] mb-1" />
            <div className="text-[12.5px] text-[hsl(var(--muted-foreground))]">Click to choose a .csv file</div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          </div>
          <div className="flex items-center gap-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 px-3 py-2">
            <span className="text-[12.5px] text-[hsl(var(--foreground))]">Costs in this file are in</span>
            <div role="group" aria-label="Import cost currency"
              className="inline-flex items-center gap-0.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-0.5">
              {[["INR", "₹ Rupees"], ["USD", "$ Dollars"]].map(([code, label]) => (
                <button key={code} type="button" onClick={() => setCostCurrency(code)}
                  aria-pressed={costCurrency === code}
                  data-testid={`import-currency-${code.toLowerCase()}`}
                  className={`h-[26px] px-2.5 rounded text-[12px] font-semibold transition-colors ${
                    costCurrency === code ? "bg-orange-600 text-white" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                  }`}>{label}</button>
              ))}
            </div>
            <span className="text-[11px] text-[hsl(var(--muted-foreground))] ml-auto">a <span className="font-mono-data">unit_cost_currency</span> column overrides this</span>
          </div>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] leading-relaxed">
            Dates in any common format are accepted (e.g. <span className="font-mono-data">28/05/2026</span> or <span className="font-mono-data">28-May-2026</span>) — ambiguous ones are read day-first. Excel quirks like a different delimiter or hidden encoding marks are handled automatically.
          </p>
          {parsed && (
            <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 px-3 py-2 text-[12.5px]">
              <div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 className="w-4 h-4" /> {parsed.valid.length} valid row{parsed.valid.length !== 1 ? "s" : ""} ready to import</div>
              {parsed.errorCount > 0 && <div className="flex items-center gap-2 text-amber-600 mt-1"><AlertTriangle className="w-4 h-4" /> {parsed.errorCount} row(s) will be skipped (missing material or quantity)</div>}
            </div>
          )}
        </div>
        <DialogFooter>
          <CancelBtn onClick={onClose} />
          <button type="button" disabled={!parsed?.valid?.length || importing} onClick={doImport}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white transition-colors">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import {parsed?.valid?.length || 0}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Notes ──
export function LotNotesDialog({ lot, onClose, onDone, canEdit = true }) {
  const [notes, setNotes] = useState(Array.isArray(lot.user_notes) ? [...lot.user_notes] : []);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/stores/lots/${lot.id}/notes`, { text: t });
      setNotes(Array.isArray(res.data?.user_notes) ? res.data.user_notes : notes);
      setText("");
      onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to add note"); }
    finally { setSaving(false); }
  };
  const sorted = [...notes].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><StickyNote className="w-5 h-5 text-orange-500" /> Notes · {lotTitle(lot)}</DialogTitle>
          <DialogDescription>{lot.heat_number ? `Heat ${lot.heat_number}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[40vh] overflow-y-auto space-y-2">
          {sorted.length === 0 ? (
            <div className="text-center text-sm text-[hsl(var(--muted-foreground))] py-4">No notes yet.</div>
          ) : sorted.map((n) => (
            <div key={n.id} className="rounded-lg border border-[hsl(var(--border))]/60 bg-[hsl(var(--muted))]/40 px-3 py-2">
              <div className="text-[13px] text-[hsl(var(--foreground))] whitespace-pre-wrap">{n.text}</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{(n.author || "").split(" ")[0]} {n.author_role ? `(${n.author_role})` : ""}</div>
            </div>
          ))}
        </div>
        {canEdit && (
          <form onSubmit={add} className="flex items-center gap-2 pt-2 border-t border-[hsl(var(--border))]/50">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note…" className={inputCls} />
            <button type="submit" disabled={saving || !text.trim()} className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
